import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { avanzoApi } from "@/lib/avanzoApi";

/**
 * Fase 6.2 · Onboarding tour para Buddy AVANZA.
 * 5 pasos con overlay ligero + welcome screen + rutina final.
 * Se dispara desde AvanzoLayout cuando el estudiante no tiene
 * `avanzo_onboarding_completed=true`. También reabrible desde Perfil.
 *
 * Diseño: overlay no bloquea toda la pantalla — un card centrado
 * con backdrop translúcido. Los "highlights" apuntan a las pestañas
 * de la navegación por id (`avz-nav-{tab}`).
 */
const TABS = [
  {
    tab: "home",
    area: "Inicio",
    title: "Aquí empieza tu preparación cada día.",
    body: "Buddy te recomendará qué trabajar según tu progreso.",
    testid: "onb-step-inicio",
  },
  {
    tab: "train",
    area: "Entrenamiento",
    title: "Este es tu entrenamiento principal.",
    body: "Completarás una sesión con Activación, apoyo de Buddy, Práctica y Reto AVANZO.",
    hint: "Si necesitas ayuda, puedes pedir una pista.",
    testid: "onb-step-entrenar",
  },
  {
    tab: "practice",
    area: "Práctica",
    title: "¿Quieres reforzar algo específico?",
    body: "Aquí puedes elegir una materia, una habilidad y practicar 5 o 10 preguntas.",
    hint: "Entrenar = Buddy recomienda · Práctica = tú eliges",
    testid: "onb-step-practica",
  },
  {
    tab: "progress",
    area: "Mi progreso",
    title: "Mira cómo estás avanzando.",
    body: "Aquí verás qué habilidades ya dominas y cuáles conviene seguir trabajando.",
    hint: "Dominio sólido · En desarrollo · Necesita práctica",
    testid: "onb-step-progreso",
  },
  {
    tab: "sim",
    area: "Simulacro",
    title: "Pon a prueba lo aprendido.",
    body: "Durante un simulacro no tendrás pistas ni ayuda del tutor.",
    testid: "onb-step-simulacro",
  },
];

export default function AvanzoOnboardingTour({ open, onClose, source = "auto" }) {
  const nav = useNavigate();
  const [phase, setPhase] = useState("welcome"); // welcome | tour | closing
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setPhase("welcome"); setStep(0); }
  }, [open]);

  // Highlight target: on tour step change, scroll to nav tab so the
  // user sees the real element behind the overlay.
  useEffect(() => {
    if (phase !== "tour") return;
    const el = document.querySelector(`[data-testid="avz-nav-${TABS[step].tab}"]`);
    if (el && el.scrollIntoView) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
    }
  }, [phase, step]);

  const complete = useCallback(async () => {
    setSaving(true);
    try { await avanzoApi.paesOnboardingComplete(); }
    catch { /* offline-safe: user can retry */ }
    setSaving(false);
    onClose?.({ completed: true });
    nav("/avanzo/entrenar");
  }, [nav, onClose]);

  const dismiss = useCallback(async () => {
    setSaving(true);
    try { await avanzoApi.paesOnboardingDismiss(); }
    catch { /* noop */ }
    setSaving(false);
    onClose?.({ completed: false });
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
      else if (e.key === "ArrowRight") {
        if (phase === "welcome") setPhase("tour");
        else if (phase === "tour" && step < TABS.length - 1) setStep((s) => s + 1);
        else if (phase === "tour") setPhase("closing");
      } else if (e.key === "ArrowLeft") {
        if (phase === "closing") { setPhase("tour"); setStep(TABS.length - 1); }
        else if (phase === "tour" && step > 0) setStep((s) => s - 1);
        else if (phase === "tour") setPhase("welcome");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, phase, step, dismiss]);

  if (!open) return null;

  return (
    <div
      className="avz-onb-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding Buddy AVANZA"
      data-testid="avz-onboarding-root"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="avz-onb-card"
        style={{
          background: "#fff", borderRadius: 12, width: "100%",
          maxWidth: 480, padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          position: "relative",
        }}
        data-testid="avz-onboarding-card"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar recorrido"
          data-testid="avz-onb-close-btn"
          style={{
            position: "absolute", top: 8, right: 10, background: "none",
            border: "none", fontSize: 24, cursor: "pointer", color: "#64748b",
          }}
        >
          ×
        </button>

        {phase === "welcome" && (
          <div data-testid="avz-onb-welcome">
            <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 600 }}>
              Bienvenido a Buddy AVANZA
            </h2>
            <p style={{ color: "#475569", fontSize: 15, lineHeight: 1.5 }}>
              Tu preparación se adapta a cómo vas avanzando. No necesitas
              hacer todo de una vez: trabaja con constancia y Buddy te irá
              guiando.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setPhase("tour")}
                data-testid="avz-onb-start-btn"
                className="avz-btn avz-btn-primary"
                style={{ flex: "1 1 auto", padding: "10px 16px" }}
              >
                Conoce cómo funciona
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="avz-onb-skip-btn"
                className="avz-btn"
                style={{
                  flex: "1 1 auto", padding: "10px 16px",
                  border: "1px solid #cbd5e1", background: "#fff",
                }}
              >
                Omitir por ahora
              </button>
            </div>
          </div>
        )}

        {phase === "tour" && (
          <div data-testid={TABS[step].testid}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 8,
                marginBottom: 6, flexWrap: "wrap",
              }}
            >
              <span
                data-testid="avz-onb-area"
                style={{
                  fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  color: "#1e4ea5", background: "#eaf1fb",
                  padding: "3px 10px", borderRadius: 999,
                }}
              >
                {TABS[step].area}
              </span>
              <span
                style={{ color: "#64748b", fontSize: 12 }}
                data-testid="avz-onb-progress"
              >
                Paso {step + 1} de {TABS.length}
              </span>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>
              {TABS[step].title}
            </h3>
            <p style={{ color: "#475569", fontSize: 15, lineHeight: 1.5, margin: 0 }}>
              {TABS[step].body}
            </p>
            {TABS[step].hint && (
              <p style={{ color: "#0369a1", fontSize: 13, marginTop: 8 }}
                 data-testid="avz-onb-hint">
                {TABS[step].hint}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button
                type="button"
                onClick={() => {
                  if (step === 0) setPhase("welcome");
                  else setStep((s) => s - 1);
                }}
                data-testid="avz-onb-back-btn"
                className="avz-btn"
                style={{ padding: "8px 14px", border: "1px solid #cbd5e1", background: "#fff" }}
              >
                ← Atrás
              </button>
              <button
                type="button"
                onClick={() => {
                  if (step === TABS.length - 1) setPhase("closing");
                  else setStep((s) => s + 1);
                }}
                data-testid="avz-onb-next-btn"
                className="avz-btn avz-btn-primary"
                style={{ marginLeft: "auto", padding: "8px 14px" }}
              >
                {step === TABS.length - 1 ? "Ver rutina" : "Siguiente →"}
              </button>
            </div>
          </div>
        )}

        {phase === "closing" && (
          <div data-testid="avz-onb-closing">
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>
              Tu rutina recomendada
            </h3>
            <ol style={{ paddingLeft: 20, color: "#334155", lineHeight: 1.7, margin: 0 }}>
              <li>Revisa <strong>Inicio</strong>.</li>
              <li>Completa tu <strong>Entrenamiento</strong>.</li>
              <li>Usa <strong>Práctica</strong> si necesitas reforzar algo.</li>
              <li>Revisa tu <strong>Progreso</strong>.</li>
              <li>Realiza <strong>Simulacros</strong> cuando estén disponibles.</li>
            </ol>
            <button
              type="button"
              onClick={complete}
              disabled={saving}
              data-testid="avz-onb-finish-btn"
              className="avz-btn avz-btn-primary"
              style={{ marginTop: 20, width: "100%", padding: "10px 16px" }}
            >
              Comenzar mi preparación
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
